var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ButtonDefinition } from "@fluentui/web-components";
import { FASTElement, attr, observable } from "@microsoft/fast-element";
import { TagEventNames } from "./tag.options.js";
/**
 * Tag
 * @summary A Tag component that displays one or two lines of text, is dismissable via a dismiss button, and can contain a preceding picture or icon before the text.
 *
 * @example
 * ```html
 * <fabric-tag
 *   dismiss-button="true"
 *   size="medium"
 *   shape="rounded"
 *   appearance="filled"
 * >
 *   <img slot="image" src="path-to-image.png" alt="Tag image" />
 *   <span slot="leadingIcon">Icon</span>
 *   <span slot="text">Primary Text</span>
 *   <span slot="textSecondary">Secondary Text</span>
 *   <span slot="dismissIcon">Dismiss</span>
 * </fabric-tag>
 * ```
 *
 * @attr {boolean} dismiss-button - Whether the tag has a dismiss button.
 * @attr {TagSize} size - The size of the tag.
 * @attr {TagShape} shape - The shape of the tag.
 * @attr {TagAppearance} appearance - The appearance of the tag.
 * @attr {boolean} disabled - Controls whether the tag is disabled.
 *
 * @prop {string} textPrimaryContent - The content of the text primary slot.
 * @prop {HTMLElement[]} textPrimarySlotted - The primary text of the tag that has been slotted.
 * @prop {HTMLElement[]} textSecondarySlotted - The secondary text of the tag.
 * @prop {TagTextSize} textSizePrimary - The text size of the tag.
 * @prop {HTMLElement[]} leadingIconSlotted - The leading icon of the tag.
 *
 * @slot image - The image of the tag.
 * @slot leadingIcon - The leading icon of the tag.
 * @slot text - The text of the tag.
 * @slot textSecondary - The secondary text of the tag.
 * @slot dismissIcon - The dismiss icon of the tag.
 * @slot - The default slot for additional tag content.
 *
 * @csspart container - The container of the tag.
 * @csspart content - The content of the tag.
 * @csspart dismissButton - The dismiss button of the tag.
 * @csspart image - Part wrapping the image slot for sizing and layout.
 * @csspart textContent - Part wrapping the text slots for layout and truncation.
 *
 * @method sizeChanged - Called when the size property changes.
 * @method textPrimarySlottedChanged - Called when the textPrimarySlotted property changes.
 * @method textSecondarySlottedChanged - Called when the textSecondarySlotted property changes.
 * @method handleTextSize - Handles the text size of the tag, which changes depending on the size of the tag.
 * @method compensateSVGSize - Sets the size of the SVG icon to match the size of the tag.
 * @method leadingIconSlottedChanged - Called when the leadingIconSlotted property changes.
 * @method emitDismissEvent - Emits the dismiss event for the tag.
 *
 * @fires dismiss - Emits when the tag is dismissed.
 *
 * @extends FASTElement
 * @tagname fabric-tag
 * @public
 */
export class Tag extends FASTElement {
    constructor() {
        super(...arguments);
        /**
         * dismissButton
         *
         * Controls whether the dismiss button is shown
         * @public
         * @type {boolean}
         * @defaultValue false
         * @attr dismiss-button
         */
        this.dismissButton = false;
        /**
         * disabled
         *
         * Controls whether the tag is disabled
         * @public
         * @type {boolean}
         */
        this.disabled = false;
        /**
         * size
         *
         * The size of the tag
         * @public
         * @type {TagSize}
         * @defaultValue "medium"
         */
        this.size = "medium";
        /**
         * shape
         *
         * The shape of the tag container
         * @public
         * @type {TagShape}
         * @defaultValue "rounded"
         */
        this.shape = "rounded";
        /**
         * appearance
         *
         * The appearance style of the tag
         * @public
         * @type {TagAppearance}
         * @defaultValue "filled"
         */
        this.appearance = "filled";
        /**
         * textPrimarySlotted
         *
         * The primary text of the tag that has been slotted
         * @public
         * @type {HTMLElement[]}
         * @defaultValue []
         */
        this.textPrimarySlotted = [];
        /**
         * The secondary text of the tag
         * @type {HTMLElement[]}
         * @defaultValue []
         * @public
         */
        this.textSecondarySlotted = [];
        /**
         * textSizePrimary
         *
         * The text size of the tag
         * @type {TagTextSize}
         * @defaultValue "body1"
         * @public
         */
        this.textSizePrimary = "body1";
        /**
         * The leading icon of the tag
         * @type {HTMLElement[]}
         * @defaultValue []
         * @public
         */
        this.leadingIconSlotted = [];
    }
    /**
     * attachEventListeners
     *
     * Reattaches dismiss button listeners when the reference changes.
     * @public
     */
    dismissButtonRefChanged() {
        this.attachEventListeners();
    }
    sizeChanged() {
        this.handleTextSize();
    }
    /**
     * @summary This method is called when the textPrimarySlotted property changes.
     * @public
     */
    textPrimarySlottedChanged() {
        var _a;
        if (this.textPrimarySlotted.length > 0) {
            this.textPrimaryContent = `Dismiss button for ${((_a = this.textPrimarySlotted[0]) === null || _a === void 0 ? void 0 : _a.textContent) || "untitled"} tag`;
        }
    }
    /**
     * textSecondarySlottedChanged
     *
     * Updates derived text size when secondary text slot content changes.
     * @public
     */
    textSecondarySlottedChanged() {
        this.handleTextSize();
    }
    /**
     * handleTextSize
     * handles the text size of the tag, which changes depending on the size of the tag
     * @public
     */
    handleTextSize() {
        var _a;
        switch (this.size) {
            case "extraSmall":
            case "small":
                this.textSizePrimary = "caption1";
                break;
            default:
                if (((_a = this.textSecondarySlotted) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                    this.textSizePrimary = "caption1";
                }
                else {
                    this.textSizePrimary = "body1";
                }
                break;
        }
    }
    /**
     * Sets the size of the SVG icon to match the size of the tag. This is necessary because otherwise the developer has to set the size of the SVG icon manually.
     * @public
     */
    compensateSVGSize() {
        var _a;
        let sizeWidth, sizeHeight;
        if (this.size === "small") {
            sizeWidth = 16;
            sizeHeight = 16;
        }
        else if (this.size === "extraSmall") {
            sizeWidth = 12;
            sizeHeight = 12;
        }
        else {
            sizeWidth = 20;
            sizeHeight = 20;
        }
        const svg = (_a = this.leadingIconSlotted[0]) === null || _a === void 0 ? void 0 : _a.querySelector("svg");
        svg === null || svg === void 0 ? void 0 : svg.setAttribute("height", sizeHeight.toString());
        svg === null || svg === void 0 ? void 0 : svg.setAttribute("width", sizeWidth.toString());
    }
    /**
     * leadingIconSlottedChanged
     *
     * Applies state for leading icon presence and updates icon sizing.
     * @public
     */
    leadingIconSlottedChanged() {
        if (this.leadingIconSlotted.length > 0) {
            this.classList.add("hasLeadingIcon");
            this.compensateSVGSize();
        }
        else {
            this.classList.remove("hasLeadingIcon");
        }
    }
    /**
     * emitDismissEvent
     *
     * @public
     *
     * emits the dismiss event for the tag
     */
    emitDismissEvent() {
        this.emitDismiss();
    }
    /**
     * attachEventListeners
     *
     * Ensures the dismiss button listener is attached and kept in sync.
     * @private
     */
    attachEventListeners() {
        var _a;
        (_a = this.events) === null || _a === void 0 ? void 0 : _a.abort();
        this.events = new AbortController();
        const { signal } = this.events;
        if (this.dismissButtonRef) {
            this.dismissButtonRef.addEventListener("click", this.emitDismissEvent.bind(this), { signal });
        }
    }
    /**
     * emitDismiss
     *
     * Emits the dismiss event.
     * @private
     */
    emitDismiss() {
        this.dispatchEvent(new CustomEvent(TagEventNames.dismiss, {
            bubbles: true,
            composed: true
        }));
    }
    /**
     * connectedCallback
     * connectedCallback for the tag
     * @public
     */
    connectedCallback() {
        super.connectedCallback();
        if (!customElements.get("fluent-button")) {
            ButtonDefinition.define(customElements);
        }
        this.attachEventListeners();
    }
    /**
     * disconnectedCallback
     *
     * Cleans up event listeners when the element disconnects.
     * @public
     */
    disconnectedCallback() {
        var _a;
        (_a = this.events) === null || _a === void 0 ? void 0 : _a.abort();
        this.events = undefined;
        super.disconnectedCallback();
    }
}
__decorate([
    observable
], Tag.prototype, "dismissButtonRef", void 0);
__decorate([
    attr({ mode: "boolean", attribute: "dismiss-button" })
], Tag.prototype, "dismissButton", void 0);
__decorate([
    attr({ mode: "boolean" })
], Tag.prototype, "disabled", void 0);
__decorate([
    attr
], Tag.prototype, "size", void 0);
__decorate([
    attr
], Tag.prototype, "shape", void 0);
__decorate([
    attr
], Tag.prototype, "appearance", void 0);
__decorate([
    observable
], Tag.prototype, "textPrimaryContent", void 0);
__decorate([
    observable
], Tag.prototype, "textPrimarySlotted", void 0);
__decorate([
    observable
], Tag.prototype, "textSecondarySlotted", void 0);
__decorate([
    observable
], Tag.prototype, "textSizePrimary", void 0);
__decorate([
    observable
], Tag.prototype, "leadingIconSlotted", void 0);
//# sourceMappingURL=tag.js.map